import { Link, Outlet, useLocation } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Mark, SegmentedControl, type IconName } from '@et/ui';
import type { Locale } from '@et/schema';
import { LanguageSelector } from './LanguageSelector.js';
import { SearchBox } from './SearchBox.js';
import { applyTheme, useSession, type Theme } from '../lib/session.js';
import { localePath } from '../lib/locale.js';
import { DocumentLocale } from './DocumentLocale.js';
import { fetchCharacterProfile, fetchMeta, queryKeys } from '../lib/api.js';
import { readConsent, writeConsent, type Consent } from '../lib/consent.js';
import { startAnalytics } from '../lib/analytics.js';
import { firebaseConfigFromEnv } from '../lib/auth.js';
import { ConsentBanner } from '../features/consent/ConsentBanner.js';

/** Ver o comentário junto ao uso, na barra. */
const SHOW_LANGUAGE_SELECTOR = false;

export function Shell({ locale }: { readonly locale: Locale }) {
  const { t } = useTranslation();
  const theme = useSession((s) => s.theme);
  const setTheme = useSession((s) => s.setTheme);
  const { pathname } = useLocation();

  /**
   * A entrada de Escrituras leva à análise da primeira figura do acervo.
   *
   * O menu já não oferece "Escrituras" como quarta análise, por isso entrar na
   * área tem de deixar alguma coisa escolhida — e a primeira figura vem da
   * ordem do conteúdo, não de um id escrito aqui. Enquanto a lista não chega,
   * a ligação aponta para a régua do corpus, que é o pior caso aceitável: uma
   * página real em vez de um destino inventado.
   */
  const meta = useQuery({ queryKey: queryKeys.meta, queryFn: fetchMeta, staleTime: 60_000 });
  const rev = meta.data === undefined
    ? null
    : { version: meta.data.contentVersion, checksum: meta.data.checksum };
  const firstFigure = useQuery({
    queryKey: rev === null ? ['figures-nav'] : queryKeys.characterProfile(rev, locale),
    queryFn: () => fetchCharacterProfile(rev!.version, locale),
    enabled: rev !== null,
    staleTime: 60_000,
  }).data?.figures[0]?.id;
  const scripturesHref = firstFigure === undefined
    ? localePath(locale, 'text')
    : localePath(locale, 'figure', firstFigure);

  /*
    Seis tarefas na barra, e so tarefas.

    Estavam oito entradas com o mesmo peso, mais o tema: "Apoiar" e
    "Metodologia" ao lado de "Explorar" como se fossem sitios onde se vai
    fazer alguma coisa. Nao sao — sao rodape. E o tema e uma preferencia, nao
    uma seccao. Tudo isso passa para o menu "Mais"; o que fica le-se de uma vez.

    As objeccoes entram porque sao a assinatura do produto e nao tinham porta:
    so se viam enterradas numa ficha ou numa claim.
  */
  const links: { href: string; label: string; icon: IconName; tone: string }[] = [
    { href: localePath(locale), label: t('nav.explore'), icon: 'explore', tone: 'evidence' },
    { href: scripturesHref, label: t('text.corpusTitle'), icon: 'corpus', tone: 'source' },
    { href: localePath(locale, 'compare'), label: t('compare.title'), icon: 'compare', tone: 'claim' },
    { href: localePath(locale, 'objections'), label: t('nav.objections'), icon: 'objection', tone: 'objection' },
    { href: localePath(locale, 'play'), label: t('play.title'), icon: 'play', tone: 'neutral' },
    { href: localePath(locale, 'learn'), label: t('learn.title'), icon: 'learn', tone: 'neutral' },
  ];

  /*
    A conta e a aprendizagem sao duas entradas, e nao uma.

    So havia "A tua aprendizagem", que abre o painel de progresso. A pagina
    onde se liga a conta Google e se escolhe o nome vive noutro endereco e
    **nenhum link em toda a app apontava para ela** — chegava-se la escrevendo
    o endereco. Quem procurasse como entrar com o Google nao encontrava.
  */
  const more: { href: string; label: string; icon: IconName }[] = [
    { href: localePath(locale, 'me'), label: t('account.yourLearning'), icon: 'me' },
    { href: localePath(locale, 'me', 'profile'), label: t('account.title'), icon: 'figure' },
    { href: localePath(locale, 'methodology'), label: t('nav.methodology'), icon: 'methodology' },
  ];
  const moreIsCurrent = more.some((l) => pathname === l.href);

  useEffect(() => { applyTheme(theme); }, [theme]);
  const [open, setOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  // Mudar de página fecha os menus: deixá-los abertos por cima do destino é a
  // forma mais fiável de fazer alguém pensar que o clique não resultou.
  useEffect(() => { setOpen(false); setMoreOpen(false); }, [pathname]);
  useEffect(() => {
    if (!moreOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (moreRef.current !== null && !moreRef.current.contains(e.target as Node)) setMoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [moreOpen]);

  /**
   * `null` enquanto ninguem respondeu, e e nesse estado que o banner aparece
   * e que nada e medido. O `readConsent` corre uma vez, no arranque: ler o
   * `localStorage` a cada render nao mudava nada e custava a cada teclada.
   */
  const [consent, setConsent] = useState<Consent | null>(() => readConsent());

  const choose = (c: Consent) => {
    writeConsent(c);
    setConsent(c);
    // Arranca ja, sem esperar por uma recarga: quem aceitou aceitou agora.
    // Recusar nao tem contrario — o modulo nunca chegou a ser carregado.
    if (c === 'granted') void startAnalytics({ config: firebaseConfigFromEnv() });
  };

  return (
    <div className="et-shell">
      <DocumentLocale locale={locale} />
      <header className="et-shell__bar">
        <div className="et-shell__brand">
          <Link to={localePath(locale)}>
            <Mark size={20} />
            <b>Evidence Timeline</b>
          </Link>
        </div>
        <button
          type="button"
          className="et-shell__burger"
          aria-expanded={open}
          aria-controls="et-shell-nav"
          onClick={() => setOpen((v) => !v)}
        >
          <Icon name={open ? 'close' : 'menu'} size={20} />
          <span className="et-visually-hidden">{t('nav.explore')}</span>
        </button>
        <nav
          id="et-shell-nav"
          className="et-shell__nav"
          aria-label={t('nav.explore')}
          data-open={open ? 'true' : 'false'}
        >
          {links.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              data-tone={l.tone}
              // `end` não existe em `Link`; a comparação exacta evita que
              // "Corpus" fique marcado quando se está numa figura.
              aria-current={pathname === l.href ? 'page' : undefined}
            >
              <Icon name={l.icon} size={16} />
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="et-shell__tools">
          <SearchBox locale={locale} rev={rev} />
          {/*
            Fora do menu de tres pontos, onde estava.

            Um produto pago por quem o usa nao pode esconder o pedido atras de
            um icone: quem quisesse apoiar tinha de abrir o menu e adivinhar
            que "Apoiar" era ali. E um botao e nao mais um link de navegacao,
            porque nao e um sitio da app — e uma accao.
          */}
          <Link className="et-shell__donate" to={localePath(locale, 'support')}>
            <Icon name="support" size={16} />
            <span>{t('nav.support')}</span>
          </Link>
          <div className="et-shell__more" ref={moreRef}>
            <button
              type="button"
              className="et-shell__moreBtn"
              aria-expanded={moreOpen}
              aria-haspopup="menu"
              aria-controls="et-shell-more"
              data-current={moreIsCurrent ? 'true' : undefined}
              onClick={() => setMoreOpen((v) => !v)}
            >
              <Icon name="more" size={18} />
              <span className="et-visually-hidden">{t('nav.more')}</span>
            </button>
            <div id="et-shell-more" className="et-shell__morePanel" role="menu" hidden={!moreOpen}>
              {more.map((l) => (
                <Link
                  key={l.href}
                  role="menuitem"
                  className="et-shell__moreLink"
                  to={l.href}
                  aria-current={pathname === l.href ? 'page' : undefined}
                >
                  <Icon name={l.icon} size={16} />
                  {l.label}
                </Link>
              ))}
              {/* Retirar o consentimento tem de ser tao possivel como da-lo.
                  Sem isto, mudar de ideias so limpando o armazenamento do
                  browser — o que na pratica e nao poder mudar de ideias. */}
              <button
                type="button"
                role="menuitem"
                className="et-shell__moreLink"
                onClick={() => { setMoreOpen(false); setConsent(null); }}
              >
                <Icon name="methodology" size={16} />
                {t('consent.menu')}
              </button>
              <div className="et-shell__moreTheme">
                <SegmentedControl<Theme>
                  label={t('a11y.themeToggle')}
                  value={theme}
                  onValueChange={setTheme}
                  options={[
                    { value: 'light', label: t('theme.light') },
                    { value: 'system', label: t('theme.system') },
                    { value: 'dark', label: t('theme.dark') },
                  ]}
                />
              </div>
              {/*
                O selector de língua está escondido, e não removido.

                As três línguas continuam a funcionar por URL — `/pt`, `/es` — e
                as traduções continuam a ser servidas e contadas na cobertura. O
                que sai do ecrã é a escolha, enquanto a prosa longa ainda vai em
                inglês na maior parte das páginas: oferecer a mudança e entregar
                metade dela é pior do que não a oferecer. Volta com uma linha.
              */}
              {SHOW_LANGUAGE_SELECTOR && <LanguageSelector current={locale} />}
            </div>
          </div>
        </div>
      </header>
      <main className="et-shell__main">
        <Outlet />
      </main>
      {consent === null && <ConsentBanner onChoose={choose} />}
    </div>
  );
}
